/**
 * @file Sums one decimal column of an analytics answer into one figure, over
 * the rows `includes` keeps. Consumers: the Overview tiles, `analyticsMonths`
 * and `analyticsGroupRows`. An absent or loading answer is loading, a refused
 * or failed one is refused, a column in `refusedColumns` is that column's
 * refusal, and a cell that is not a decimal string fails with a message
 * rather than throwing, so no unanswered figure ever reads as zero.
 */
import type { Snapshot } from 'valtio';

import type { NamedQueryCell } from '#types';

import { analyticsDecimalFraction, analyticsDecimalSum } from '#types';
import { analyticsRefusedColumn } from './analyticsRefusedColumn';
import { analyticsUnansweredFigure } from './analyticsUnansweredFigure';
import type { AnalyticsAnswer, AnalyticsDecimalFigure } from './index.types';

export const analyticsAnswerFigure = (
  answer: Snapshot<AnalyticsAnswer> | undefined,
  column: string,
  includes: (row: Snapshot<Record<string, NamedQueryCell>>) => boolean = () =>
    true,
): AnalyticsDecimalFigure => {
  if (answer?.status !== 'answered') return analyticsUnansweredFigure(answer);
  const { result } = answer;
  const refused = analyticsRefusedColumn(result, column);
  if (refused !== null)
    return { status: 'refusedColumn', column: refused };
  const cells = result.rows.filter(includes).map((row) => row[column]);
  const decimals = cells.filter(
    (cell): cell is string =>
      typeof cell === 'string' && analyticsDecimalFraction(cell) !== undefined,
  );
  if (decimals.length !== cells.length)
    return {
      status: 'refused',
      answer: {
        status: 'failed',
        message: `The ${result.query} answer holds no decimal ${column}.`,
      },
    };
  return { status: 'answered', decimal: analyticsDecimalSum(decimals) };
};
